import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { NavLink } from 'react-router-dom';

import { fetchWithAuth } from '../../../utils/api';
import { Heading } from '../../ui/heading';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { ScrollArea } from '../../ui/scroll-area';
import { Separator } from '../../ui/separator';
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  CalendarIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  HashtagIcon,
} from '@heroicons/react/24/outline';

interface TaxonomyVersion {
  id: string;
  version: string;
  is_active: boolean;
  content_hash?: string;
  created_at: string;
  updated_at: string;
  statistics: {
    dimensions: number;
    total_labels: number;
  };
}

interface OrgActiveTaxonomyMeta {
  version: string | null;
  isOrganizationSpecific: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  statistics: {
    dimensions: number;
    total_labels: number;
  };
}

const TaxonomySettings: React.FC = () => {
  const [versions, setVersions] = useState<TaxonomyVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [orgActive, setOrgActive] = useState<OrgActiveTaxonomyMeta | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    Promise.all([fetchTaxonomyVersions(), fetchOrgActiveTaxonomy()]).finally(() => {});
  }, []);

  const fetchTaxonomyVersions = async () => {
    try {
      setLoading(true);
      setError(null);

      const orgData = await fetchWithAuth('/v1/classify/taxonomy/org-versions').catch(() => []);
      if (Array.isArray(orgData) && orgData.length > 0) {
        setVersions(orgData);
      } else {
        const globalData = await fetchWithAuth('/v1/classify/taxonomy/versions');
        setVersions(globalData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgActiveTaxonomy = async () => {
    try {
      const response = await fetchWithAuth('/v1/classify/taxonomy');
      const versionInfo = response?.version_info as
        | {
            version: string | null;
            created_at: string | null;
            updated_at: string | null;
            is_organization_specific: boolean;
          }
        | undefined;

      const data = response?.data as { dimensions?: Array<{ categories?: Array<{ labels?: any[] }> }> } | null;
      const dimensions = Array.isArray(data?.dimensions) ? data!.dimensions.length : 0;
      const totalLabels = Array.isArray(data?.dimensions)
        ? data!.dimensions.reduce((sum: number, dim: any) => {
            const categories = Array.isArray(dim.categories) ? dim.categories : [];
            return (
              sum +
              categories.reduce((categorySum: number, category: any) => {
                return categorySum + (Array.isArray(category.labels) ? category.labels.length : 0);
              }, 0)
            );
          }, 0)
        : 0;

      setOrgActive({
        version: versionInfo?.version ?? null,
        isOrganizationSpecific: !!versionInfo?.is_organization_specific,
        createdAt: versionInfo?.created_at ?? null,
        updatedAt: versionInfo?.updated_at ?? null,
        statistics: {
          dimensions,
          total_labels: totalLabels,
        },
      });
    } catch (err) {
      console.error('Failed to fetch organization taxonomy:', err);
    }
  };

  const handleReloadTaxonomy = async () => {
    try {
      setReloading(true);
      setError(null);
      setSuccessMessage(null);

      const data = await fetchWithAuth('/v1/classify/taxonomy/reload', {
        method: 'POST',
      });

      setSuccessMessage(data.message);
      await Promise.all([fetchTaxonomyVersions(), fetchOrgActiveTaxonomy()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setReloading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      setError('Please select a JSON file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);

      const fileContent = await file.text();
      let taxonomyData;

      try {
        taxonomyData = JSON.parse(fileContent);
      } catch {
        setError('Invalid JSON file format');
        return;
      }

      const data = await fetchWithAuth('/v1/classify/taxonomy/upload', {
        method: 'POST',
        body: JSON.stringify(taxonomyData),
      });

      setSuccessMessage(data.message);
      await Promise.all([fetchTaxonomyVersions(), fetchOrgActiveTaxonomy()]);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'object') {
          const detail = err.response.data.detail;
          if (detail.errors && Array.isArray(detail.errors)) {
            setError(`${detail.message}: ${detail.errors.join(', ')}`);
          } else {
            setError(detail.message || 'Upload failed');
          }
        } else {
          setError(err.response.data.detail);
        }
      } else {
        setError(err.message || 'Failed to upload taxonomy');
      }
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[32rem] items-center justify-center rounded-lg border border-border bg-card shadow-[var(--shadow-card)]">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent">
            <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Loading taxonomy settings</h3>
          <p className="mt-2 text-[13px] leading-[1.7] text-muted-foreground">
            Preparing version history and organization overrides.
          </p>
        </div>
      </div>
    );
  }

  const globalActive = versions.find((version) => version.is_active);

  return (
    <div className="space-y-4">
      <div className="mb-2 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <Heading level={1} weight="bold" className="text-[28px] tracking-[-0.03em]">
            Taxonomy management
          </Heading>
          <p className="mt-1 max-w-2xl text-[13px] leading-[1.7] text-muted-foreground">
            Manage GO Conduct Intelligence Framework versions, organization overrides, and JSON uploads from one
            place.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={handleReloadTaxonomy} disabled={reloading}>
            <ArrowPathIcon className={`h-4 w-4 ${reloading ? 'animate-spin' : ''}`} />
            {reloading ? 'Reloading' : 'Reload taxonomy'}
          </Button>
          <Button variant="default" size="sm" onClick={() => fileInputRef.current?.click()}>
            <ArrowUpTrayIcon className={`h-4 w-4 ${uploading ? 'animate-pulse' : ''}`} />
            {uploading ? 'Uploading' : 'Upload JSON'}
          </Button>
        </div>
      </div>

      <nav className="mb-6 flex gap-3">
        <NavLink
          to="/v3/taxonomy"
          className={({ isActive }) =>
            [
              'flex items-center gap-2 rounded-full border px-3 py-2 text-[13px] font-semibold transition-[background-color,color,border-color,box-shadow]',
              isActive
                ? 'border-primary/15 bg-[var(--go-good-vibes-tint)] text-primary shadow-[var(--shadow-card)]'
                : 'border-border bg-background text-muted-foreground hover:border-primary/15 hover:text-foreground',
            ].join(' ')
          }
        >
          Visualization
        </NavLink>
        <NavLink
          to="/v3/settings/taxonomy"
          className={({ isActive }) =>
            [
              'flex items-center gap-2 rounded-full border px-3 py-2 text-[13px] font-semibold transition-[background-color,color,border-color,box-shadow]',
              isActive
                ? 'border-primary/15 bg-[var(--go-good-vibes-tint)] text-primary shadow-[var(--shadow-card)]'
                : 'border-border bg-background text-muted-foreground hover:border-primary/15 hover:text-foreground',
            ].join(' ')
          }
        >
          Settings
        </NavLink>
      </nav>

      {successMessage && (
        <Card className="border-[var(--go-success)]/20 bg-[color:rgba(22,163,74,0.06)]">
          <CardContent className="flex items-start gap-3 px-4 py-4">
            <CheckCircleIcon className="mt-0.5 h-5 w-5 text-[var(--go-success)]" />
            <div className="text-[13px] leading-[1.7] text-foreground">{successMessage}</div>
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className="border-destructive/20 bg-destructive/5">
          <CardContent className="flex items-start gap-3 px-4 py-4">
            <ExclamationCircleIcon className="mt-0.5 h-5 w-5 text-destructive" />
            <div className="text-[13px] leading-[1.7] text-foreground">{error}</div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-4">
          {(orgActive?.version || globalActive) && (
            <Card className="border-border/80 shadow-[var(--shadow-card)]">
              <CardHeader className="px-5 py-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <CardTitle className="text-[15px]">Active taxonomy version</CardTitle>
                    <CardDescription>Current version in effect for classification.</CardDescription>
                  </div>
                  {orgActive?.version && <Badge variant="default">Organization-specific</Badge>}
                </div>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <TaxonomyStat label="Version" value={orgActive?.version ?? globalActive?.version ?? 'N/A'} mono />
                <TaxonomyStat
                  label="Dimensions"
                  value={orgActive ? orgActive.statistics.dimensions : (globalActive?.statistics.dimensions ?? 0)}
                />
                <TaxonomyStat
                  label="Total labels"
                  value={orgActive ? orgActive.statistics.total_labels : (globalActive?.statistics.total_labels ?? 0)}
                />
                <TaxonomyStat
                  label="Last updated"
                  value={
                    orgActive?.updatedAt
                      ? format(new Date(orgActive.updatedAt), 'MMM dd, yyyy HH:mm')
                      : globalActive?.updated_at
                        ? format(new Date(globalActive.updated_at), 'MMM dd, yyyy HH:mm')
                        : '-'
                  }
                />
              </CardContent>
            </Card>
          )}

          <Card className="border-border/80 shadow-[var(--shadow-card)]">
            <CardHeader className="px-5 py-5">
              <CardTitle className="text-[15px]">Taxonomy actions</CardTitle>
              <CardDescription>
                Reload or replace the current framework without leaving the settings screen.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3 rounded-xl border border-border bg-background p-4">
                <div>
                  <h3 className="text-[13px] font-semibold text-foreground">Reload taxonomy</h3>
                  <p className="mt-1 text-[13px] leading-[1.7] text-muted-foreground">
                    Check for updates to the taxonomy JSON and create a new version if it has changed.
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleReloadTaxonomy} disabled={reloading}>
                  <ArrowPathIcon className={`h-4 w-4 ${reloading ? 'animate-spin' : ''}`} />
                  {reloading ? 'Reloading...' : 'Reload taxonomy'}
                </Button>
              </div>

              <Separator />

              <div className="space-y-3 rounded-xl border border-border bg-background p-4">
                <div>
                  <h3 className="text-[13px] font-semibold text-foreground">Upload new taxonomy</h3>
                  <p className="mt-1 text-[13px] leading-[1.7] text-muted-foreground">
                    Upload a JSON file with a new version number to replace the current taxonomy definition.
                  </p>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="sr-only"
                  id="taxonomy-upload"
                />
                <Button variant="default" size="sm" onClick={() => fileInputRef.current?.click()}>
                  <ArrowUpTrayIcon className={`h-4 w-4 ${uploading ? 'animate-pulse' : ''}`} />
                  {uploading ? 'Uploading...' : 'Upload taxonomy JSON'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/80 shadow-[var(--shadow-card)]">
            <CardHeader className="px-5 py-5">
              <CardTitle className="text-[15px]">Version history</CardTitle>
              <CardDescription>Audit the active and historical taxonomy releases.</CardDescription>
            </CardHeader>
            <CardContent className="px-0 pb-0">
              <ScrollArea className="h-[28rem]">
                <div className="divide-y divide-border">
                  {versions.length === 0 ? (
                    <div className="px-5 py-8 text-center text-[13px] leading-[1.7] text-muted-foreground">
                      No taxonomy versions found. Reload taxonomy to initialize the list.
                    </div>
                  ) : (
                    versions.map((version) => (
                      <div key={version.id} className="px-5 py-5 transition-colors hover:bg-accent/30">
                        <div className="mb-3 flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-[15px] font-semibold text-foreground">v{version.version}</span>
                            {version.is_active && <Badge variant="success">Active</Badge>}
                          </div>
                          {version.content_hash && (
                            <span className="font-mono text-[11px] text-muted-foreground">
                              {version.content_hash.substring(0, 8)}...
                            </span>
                          )}
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                          <InlineStat icon={<HashtagIcon className="h-4 w-4" />} label={`${version.statistics.dimensions} dimensions`} />
                          <InlineStat icon={<DocumentTextIcon className="h-4 w-4" />} label={`${version.statistics.total_labels} labels`} />
                          <InlineStat
                            icon={<CalendarIcon className="h-4 w-4" />}
                            label={format(new Date(version.created_at), 'MMM dd, yyyy')}
                          />
                          <InlineStat
                            icon={<CalendarIcon className="h-4 w-4" />}
                            label={format(new Date(version.updated_at), 'MMM dd, yyyy')}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        <Card className="h-fit border-border/80 shadow-[var(--shadow-card)]">
          <CardHeader className="px-5 py-5">
            <CardTitle className="text-[15px]">About taxonomy versions</CardTitle>
            <CardDescription>Why this page exists and how the active version is chosen.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-[13px] leading-[1.7] text-muted-foreground">
            <p>
              The GO Conduct Intelligence Framework taxonomy classifies signals across complaint detection,
              vulnerability assessment, customer strain, and foreseeable harm.
            </p>
            <p>
              Changes to the taxonomy structure are tracked through version history so you can review the active
              framework and understand when organization-specific overrides are in effect.
            </p>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2">
                <span>Organization-specific active taxonomy</span>
                <Badge variant={orgActive?.version ? 'default' : 'secondary'}>
                  {orgActive?.version ? 'Enabled' : 'Inherited'}
                </Badge>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2">
                <span>Dimensions visible</span>
                <span className="font-semibold text-foreground">{orgActive?.statistics.dimensions ?? globalActive?.statistics.dimensions ?? 0}</span>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2">
                <span>Labels visible</span>
                <span className="font-semibold text-foreground">{orgActive?.statistics.total_labels ?? globalActive?.statistics.total_labels ?? 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TaxonomySettings;

const TaxonomyStat: React.FC<{
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}> = ({ label, value, mono = false }) => (
  <div className="rounded-lg border border-border bg-background px-4 py-3">
    <div className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">{label}</div>
    <div className={`mt-1 text-[15px] font-semibold tracking-[-0.01em] text-foreground ${mono ? 'font-mono' : ''}`}>
      {value}
    </div>
  </div>
);

const InlineStat: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2 text-[13px] text-muted-foreground">
    <span className="text-foreground/80">{icon}</span>
    <span>{label}</span>
  </div>
);
